/**
 * Email Campaign Stats Page
 * Route: /marketing/campaigns/:id/stats
 *
 * Faithful replica of the PHP stats.php — header with date range,
 * 8-card stat summary, Recharts area chart, click tracking table
 * with click-map overlay in the email content modal, and details grid.
 *
 * The Replies card is the only clickable one: it links to the contact list
 * filtered to the people who answered, rather than opening a separate list of
 * replies. A reply belongs beside that contact's delivery, opens and clicks.
 */
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Copy, MoreVertical, Play } from 'lucide-react';
import { CampaignDailyChart } from './CampaignDailyChart';
import { apiClient } from '@/services/apiClient';
import { cloneCampaign, resumeEmailCampaign } from '@/services/campaigns.service';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import { CloneCampaignModal } from './CloneCampaignModal';
import { DatePicker } from '@/components/ui/DatePicker';
import { MarketingHistoryCard } from '@/components/marketing/MarketingHistoryCard';
import { leadCountsOf } from '@/services/marketing.service';
import type { RequestInfoLeadCounts } from '@/services/marketing.service';
import { statBox, contactsPath } from '@/components/marketing/statBox';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StatsDetail {
  emailsTriggered: number;
  delivered: number;
  uniqueOpens: number;
  uniqueClicks: number;
  replies: number;
  bounces: number;
  unsubscribes: number;
  spamReports: number;
  /** Replies we could not tie to a patient — they replied from another address. */
  repliesUnattributed: number;
}

interface DailyStat {
  date: string;
  opens: number;
  clicks: number;
}

interface ClickTrackingItem {
  url: string;
  clicks: number;
  total: number;
}

interface CampaignList {
  id: number;
  name: string;
  description?: string;
}

interface StatsResponse {
  campaign: Record<string, any>;
  stats: StatsDetail;
  dailyStats: DailyStat[];
  clickTracking: ClickTrackingItem[];
  lists: CampaignList[];
  contactsCount: number;
  queuedCount: number;
  /** Request-info leads this campaign produced. Absent from older API builds. */
  leads?: RequestInfoLeadCounts;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// `sent_at` arrives as a Pacific wall-clock DATETIME string
// (config('app.timezone')) with no offset; reproject into the viewer's zone
// and suffix the abbreviation so the send time is unambiguous.
function formatSentDate(dateStr: string | null | undefined, tz: string): string {
  return formatPacificDateTime(dateStr, tz, { weekday: true });
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.origin + u.pathname.replace(/\/+$/, '') + u.search + u.hash;
  } catch {
    return url.replace(/\/+$/, '');
  }
}

// ─── Stat Card Row ───────────────────────────────────────────────────────────

/** The lead tile reads the top-level `leads` counts, not `stats`. */
type StatCardKey = keyof StatsDetail | 'leads';

const STAT_CARDS: {
  key: StatCardKey;
  label: string;
  color: string;
  warnWhenPositive?: boolean;
  /**
   * The contacts list's filters for the people behind the number. The list
   * filters all time, while opens, clicks, replies, bounces, spam and leads
   * here follow the date range, so their lists can run longer.
   */
  link: Record<string, string>;
}[] = [
  { key: 'emailsTriggered', label: 'Emails Sent', color: '#00aff0', link: {} },
  { key: 'delivered', label: 'Delivered', color: '#8e44ad', link: { filter: 'delivered' } },
  { key: 'uniqueOpens', label: 'Unique Opens', color: '#28a745', link: { filter: 'opened' } },
  { key: 'uniqueClicks', label: 'Unique Clicks', color: '#ffc107', link: { filter: 'clicked' } },
  // Distinct repliers, so it reads on the same basis as the two unique counts
  // beside it. Teal matches how a marketing reply is styled in the Comm Log.
  { key: 'replies', label: 'Replies', color: '#17a2b8', link: { filter: 'replied' } },
  { key: 'bounces', label: 'Bounces', color: '#dc3545', warnWhenPositive: true, link: { filter: 'bounce' } },
  { key: 'unsubscribes', label: 'Unsubscribes', color: '#dc3545', warnWhenPositive: true, link: { filter: 'unsubscribed' } },
  { key: 'spamReports', label: 'Spam Reports', color: '#dc3545', warnWhenPositive: true, link: { filter: 'spam' } },
  // Request-info leads: the campaign's own recipients and whoever they
  // forwarded the link to, together; opens "Created a lead" (either kind).
  { key: 'leads', label: 'Leads', color: '#fd7e14', link: { lead: '1' } },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function EmailCampaignStatsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaignId = Number(id);
  const userTz = useUserTimezone();

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Content modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'html' | 'plain'>('html');
  const [clickMapEnabled, setClickMapEnabled] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Clone + overflow menu
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManageSend = (user?.userLevel ?? 0) >= 5;
  const cloneMut = useMutation({
    mutationFn: (cloneName: string) => cloneCampaign(campaignId, cloneName),
    onSuccess: (data) => {
      toast.success('Campaign cloned successfully');
      setShowCloneModal(false);
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      navigate(`/marketing/campaigns/${data.id}`);
    },
    onError: () => toast.error('Failed to clone campaign'),
  });

  const resumeMut = useMutation({
    mutationFn: () => resumeEmailCampaign(campaignId),
    onSuccess: (res) => {
      toast.success(res.message);
      setConfirmResume(false);
      qc.invalidateQueries({ queryKey: ['campaign-stats-page', campaignId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to resume campaign');
      setConfirmResume(false);
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-stats-page', campaignId, dateFrom, dateTo],
    queryFn: async (): Promise<StatsResponse> => {
      const res = await apiClient.get(`/marketing/email-campaigns/${campaignId}/stats`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      return res.data.data ?? res.data;
    },
    enabled: !!campaignId,
    // Sends are asynchronous: poll while the campaign is 'sending' (same cadence
    // as the SMS stats page) and stop as soon as it settles.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.campaign?.status === 'sending') return 30000;
      return false;
    },
  });

  const campaign = data?.campaign;
  const stats = data?.stats;
  const dailyStats = data?.dailyStats ?? [];
  const clickTracking = data?.clickTracking ?? [];
  const lists = data?.lists ?? [];
  const contactsCount = data?.contactsCount ?? 0;
  const queuedCount = data?.queuedCount ?? 0;
  const hasQueued = queuedCount > 0;

  const totalClicks = useMemo(
    () => clickTracking.reduce((sum, ct) => sum + ct.clicks, 0),
    [clickTracking],
  );

  // ── Click Map Logic ──────────────────────────────────────────────────────

  const applyClickMap = useCallback(
    (enabled: boolean) => {
      const frame = iframeRef.current;
      if (!frame) return;
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (!doc?.body) return;

      // Remove existing badges
      doc.querySelectorAll('.clickmap-badge').forEach((el) => el.remove());
      // Reset link styles
      doc.querySelectorAll('a[data-clickmap-styled]').forEach((a: any) => {
        a.style.outline = '';
        a.style.outlineOffset = '';
        a.removeAttribute('data-clickmap-styled');
      });

      if (!enabled) return;

      // Build lookup
      const urlMap: Record<string, { clicks: number; total: number }> = {};
      clickTracking.forEach((item) => {
        const norm = normalizeUrl(item.url);
        if (!urlMap[norm]) urlMap[norm] = { clicks: 0, total: 0 };
        urlMap[norm].clicks += item.clicks;
        urlMap[norm].total += item.total;
      });

      // Inject CSS
      const styleId = 'clickmap-style';
      if (!doc.getElementById(styleId)) {
        const style = doc.createElement('style');
        style.id = styleId;
        style.textContent = [
          '.clickmap-badge {',
          '  position: absolute; display: flex; align-items: center; gap: 3px;',
          '  padding: 2px 7px; color: #fff;',
          '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
          '  font-size: 11px; font-weight: 700; border-radius: 10px;',
          '  white-space: nowrap; pointer-events: none; z-index: 99999;',
          '  box-shadow: 0 2px 6px rgba(0,0,0,0.25); line-height: 1.2;',
          '}',
          '.clickmap-badge.has-clicks { background: rgba(0, 175, 240, 0.92); }',
          '.clickmap-badge.no-clicks  { background: rgba(153, 153, 153, 0.85); }',
          '.clickmap-badge svg { width: 10px; height: 10px; fill: currentColor; flex-shrink: 0; }',
        ].join('\n');
        doc.head.appendChild(style);
      }

      doc.querySelectorAll('a[href]').forEach((link: any) => {
        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:')) return;

        const norm = normalizeUrl(href);
        const d = urlMap[norm] || { clicks: 0, total: 0 };
        const hasClicks = d.clicks > 0;

        const parent = link.parentElement;
        if (parent && getComputedStyle(parent).position === 'static') {
          parent.style.position = 'relative';
        }

        link.style.position = 'relative';
        link.setAttribute('data-clickmap-styled', '1');
        link.style.outline = hasClicks
          ? '2px solid rgba(0, 175, 240, 0.6)'
          : '2px solid rgba(153, 153, 153, 0.4)';
        link.style.outlineOffset = '2px';

        const badge = doc.createElement('span');
        badge.className = 'clickmap-badge ' + (hasClicks ? 'has-clicks' : 'no-clicks');
        badge.innerHTML =
          '<svg viewBox="0 0 24 24"><path d="M15 15l-2 5L9 9l11 4-5 2z"/><path d="M22 22l-5-5" fill="none" stroke="currentColor" stroke-width="2"/></svg>' +
          d.clicks +
          (d.clicks === 1 ? ' click' : ' clicks');
        badge.style.position = 'absolute';
        badge.style.top = '-8px';
        badge.style.right = '-8px';
        link.appendChild(badge);
      });
    },
    [clickTracking],
  );

  const handleClickMapToggle = useCallback(
    (enabled: boolean) => {
      setClickMapEnabled(enabled);
      const frame = iframeRef.current;
      if (!frame) return;
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (doc?.body && doc.body.innerHTML.length > 10) {
        applyClickMap(enabled);
      } else {
        const handler = () => {
          frame.removeEventListener('load', handler);
          applyClickMap(enabled);
        };
        frame.addEventListener('load', handler);
      }
    },
    [applyClickMap],
  );

  const openContentModal = useCallback(
    (withClickMap = false) => {
      setModalOpen(true);
      setModalTab('html');
      if (withClickMap) {
        setClickMapEnabled(true);
        setTimeout(() => {
          const frame = iframeRef.current;
          if (!frame) return;
          const doc = frame.contentDocument || frame.contentWindow?.document;
          if (doc?.body && doc.body.innerHTML.length > 10) {
            applyClickMap(true);
          } else {
            const handler = () => {
              frame.removeEventListener('load', handler);
              applyClickMap(true);
            };
            frame.addEventListener('load', handler);
          }
        }, 100);
      } else {
        setClickMapEnabled(false);
      }
    },
    [applyClickMap],
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = modalOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  // Close overflow menu on outside click
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflowMenu]);

  // ── Loading / Error ──────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!campaign || !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: '#999' }}>
        <p style={{ fontSize: 18 }}>Campaign not found.</p>
        <button
          type="button"
          onClick={() => navigate('/marketing/email-campaigns')}
          style={{ marginTop: 16, color: '#00aff0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  const htmlContent = campaign.html_content ?? campaign.emailBody ?? '';
  const plainContent = campaign.plain_content ?? campaign.plainContent ?? '';
  const sentDate = campaign.sent_at ?? campaign.sentAt ?? campaign.updated_at ?? campaign.updatedAt;
  const statusBadgeClass =
    campaign.status === 'sent'
      ? 'stat-badge-sent'
      : campaign.status === 'scheduled'
        ? 'stat-badge-scheduled'
        : campaign.status === 'sending'
          ? 'stat-badge-sending'
          : 'stat-badge-default';

  // Missing `leads` (an API that predates request-info links) reads as zeros.
  const leads = leadCountsOf(data);
  const cardValues: Record<StatCardKey, number> = {
    ...stats,
    leads: leads.recipient + leads.referral,
  };

  return (
    <>
      <style>{`
        .esp-page { width: 100%; padding: 20px; margin: 0 auto; }

        .esp-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e5e5e5;
          /* Pinned to the very top: the negative margin swallows the page's 20px
             top padding so the header starts flush at scroll 0 and never travels;
             opaque so content slides underneath. */
          position: sticky; top: 0; z-index: 100; background: #fff;
          margin-top: -20px; padding-top: 20px;
        }
        .esp-header h1 { margin: 0 0 8px 0; font-size: 24px; font-weight: 600; color: #333; }
        .esp-meta { display: flex; align-items: center; gap: 15px; font-size: 14px; color: #666; }

        .esp-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 12px; font-size: 12px;
          font-weight: 600; text-transform: capitalize;
        }
        .esp-badge::before {
          content: ''; width: 8px; height: 8px; border-radius: 50%; background: currentColor;
        }
        .stat-badge-sent { background: #d4edda; color: #155724; }
        .stat-badge-scheduled { background: #fff3cd; color: #856404; }
        .stat-badge-sending { background: #cce5ff; color: #004085; }
        .stat-badge-default { background: #e9ecef; color: #666; }

        .esp-header-right {
          display: flex; flex-direction: column; align-items: flex-end; gap: 12px;
        }
        .esp-date-form { display: flex; gap: 10px; align-items: center; font-size: 13px; color: #666; }
        .esp-date-input {
          padding: 4px 8px; border: 1px solid #ddd; border-radius: 5px;
          font-size: 13px; background: white;
        }

        .esp-summary {
          /* 9 cards: one row when wide, 3x3 below 1200px, two to a row on a
             phone (the last row runs one short). Keep the count and the
             breakpoints in step. */
          display: grid; grid-template-columns: repeat(9, 1fr); gap: 0;
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .esp-stat-card {
          padding: 20px 15px; text-align: center; border-right: 1px solid #e5e5e5;
        }
        .esp-stat-card:last-child { border-right: none; }
        .esp-replies-note {
          font-size: 12px; color: #666; margin: -18px 0 25px; padding: 0 2px;
        }
        .esp-stat-value { font-size: 28px; font-weight: 600; margin-bottom: 6px; }
        .esp-stat-value.zero { color: #999; }
        .esp-stat-label {
          font-size: 11px; font-weight: 600; text-transform: uppercase;
          color: #666; letter-spacing: 0.5px;
        }

        .esp-chart-container {
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          padding: 20px; margin-bottom: 25px;
        }

        .esp-section {
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .esp-section-header {
          display: flex; align-items: center; gap: 8px;
          padding: 15px 20px; border-bottom: 1px solid #e5e5e5;
          font-weight: 600; color: #333;
        }
        .esp-info-icon {
          width: 16px; height: 16px; border-radius: 50%; background: #e5e5e5;
          color: #666; font-size: 11px; display: flex; align-items: center;
          justify-content: center; cursor: help;
        }

        .esp-table { width: 100%; border-collapse: collapse; }
        .esp-table th {
          text-align: left; padding: 12px 20px; font-size: 11px; font-weight: 600;
          text-transform: uppercase; color: #666; background: #f8f9fa;
          border-bottom: 1px solid #e5e5e5;
        }
        .esp-table th:last-child, .esp-table td:last-child { text-align: right; }
        .esp-table td {
          padding: 15px 20px; border-bottom: 1px solid #f0f0f0; font-size: 14px; color: #333;
        }
        .esp-table tr:last-child td { border-bottom: none; }

        .esp-rank-badge {
          display: inline-flex; align-items: center; justify-content: center;
          width: 24px; height: 24px; border-radius: 4px; font-size: 12px; font-weight: 600;
        }
        .esp-rank-1 { background: #ffd700; color: #333; }
        .esp-rank-2 { background: #c0c0c0; color: #333; }
        .esp-rank-3 { background: #cd7f32; color: white; }
        .esp-rank-default { background: #e9ecef; color: #666; }

        .esp-link-url {
          color: #00aff0; text-decoration: none; word-break: break-all;
        }
        .esp-link-url:hover { text-decoration: underline; }

        .esp-table-footer {
          display: flex; justify-content: space-between; padding: 12px 20px;
          background: #f8f9fa; font-size: 13px; color: #666;
        }
        .esp-table-footer strong { color: #333; }

        .esp-details-grid { display: grid; grid-template-columns: 200px 1fr; gap: 0; }
        .esp-detail-label {
          padding: 15px 20px; font-weight: 600; color: #333;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .esp-detail-value {
          padding: 15px 20px; color: #666; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .esp-detail-row:last-child .esp-detail-label,
        .esp-detail-row:last-child .esp-detail-value { border-bottom: none; }
        .esp-detail-value a { color: #00aff0; text-decoration: none; }
        .esp-detail-value a:hover { text-decoration: underline; }

        .esp-list-tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .esp-list-tag {
          display: inline-block; padding: 4px 10px; background: #e7f3ff;
          color: #00aff0; border-radius: 4px; font-size: 13px;
          text-decoration: none; transition: background 0.2s;
        }
        .esp-list-tag:hover { background: #d0eaff; text-decoration: none; }

        .esp-empty-state { padding: 40px 20px; text-align: center; color: #999; }

        .esp-contacts-link {
          display: inline-flex; align-items: center; gap: 8px; text-decoration: none;
        }
        .esp-contacts-link:hover { opacity: 0.85; }

        .esp-clickmap-btn {
          margin-left: auto; padding: 6px 12px; font-size: 12px; font-weight: 600;
          color: #00aff0; background: #e7f3ff; border: 1px solid #b8dff7;
          border-radius: 5px; cursor: pointer; transition: background 0.2s;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .esp-clickmap-btn:hover { background: #d0eaff; }

        .esp-overflow-wrap {
          position: relative;
        }
        .esp-overflow-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border: 1px solid #ddd; border-radius: 5px;
          background: white; cursor: pointer; color: #666; transition: background 0.2s;
        }
        .esp-overflow-btn:hover { background: #f5f5f5; }
        .esp-overflow-menu {
          position: absolute; right: 0; top: 100%; margin-top: 4px; z-index: 50;
          min-width: 170px; background: white; border: 1px solid #e5e5e5;
          border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          padding: 4px 0;
        }
        .esp-overflow-item {
          display: flex; align-items: center; gap: 8px; width: 100%;
          padding: 8px 14px; border: none; background: none;
          font-size: 13px; color: #333; cursor: pointer; text-align: left;
        }
        .esp-overflow-item:hover { background: #f5f5f5; }

        /* Campaign Actions */
        .esp-actions {
          display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
          background: #fff8f0; border: 1px solid #ffe0c2; border-radius: 8px;
          padding: 12px 16px; margin-bottom: 20px;
        }
        .esp-actions-title { font-size: 13px; font-weight: 700; color: #b45309; text-transform: uppercase; letter-spacing: 0.5px; }
        .esp-actions-note { font-size: 13px; color: #92400e; }
        .esp-action-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border: none; border-radius: 6px;
          font-size: 13px; font-weight: 600; cursor: pointer; color: #fff;
        }
        .esp-action-btn:disabled { opacity: 0.6; cursor: default; }
        .esp-action-btn--resume { background: #f97316; margin-left: auto; }
        .esp-action-btn--resume:hover:not(:disabled) { background: #ea580c; }

        /* Confirm dialog */
        .esp-confirm-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 10000; justify-content: center; align-items: center;
        }
        .esp-confirm-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 440px;
          padding: 24px; box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .esp-confirm-modal h3 { margin: 0 0 12px; font-size: 17px; font-weight: 600; color: #333; }
        .esp-confirm-modal p { margin: 0 0 20px; font-size: 14px; color: #555; line-height: 1.6; }
        .esp-confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }
        .esp-confirm-cancel {
          padding: 8px 16px; border: 1px solid #ddd; border-radius: 6px;
          background: #fff; font-size: 13px; font-weight: 600; color: #555; cursor: pointer;
        }
        .esp-confirm-cancel:hover { background: #f5f5f5; }

        /* Modal */
        .esp-modal-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: center;
        }
        .esp-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 800px;
          max-height: 85vh; display: flex; flex-direction: column;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .esp-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .esp-modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .esp-modal-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; padding: 0 4px; line-height: 1;
        }
        .esp-modal-close:hover { color: #333; }
        .esp-modal-tabs {
          display: flex; align-items: center; border-bottom: 1px solid #e5e5e5; padding: 0 20px;
        }
        .esp-modal-tab {
          padding: 10px 18px; border: none; background: none; cursor: pointer;
          font-size: 13px; font-weight: 600; color: #999;
          border-bottom: 2px solid transparent; margin-bottom: -1px;
        }
        .esp-modal-tab.active { color: #00aff0; border-bottom-color: #00aff0; }
        .esp-modal-body { flex: 1; overflow: auto; padding: 0; }
        .esp-modal-panel { display: none; height: 100%; }
        .esp-modal-panel.active { display: block; }
        .esp-modal-panel iframe { width: 100%; height: 500px; border: none; }
        .esp-modal-panel pre {
          margin: 0; padding: 20px; white-space: pre-wrap; word-wrap: break-word;
          font-size: 13px; color: #333; background: #f8f9fa; min-height: 200px;
        }

        /* Click Map Toggle */
        .esp-clickmap-toggle-wrap {
          display: flex; align-items: center; gap: 8px; margin-left: auto;
        }
        .esp-clickmap-toggle-label {
          font-size: 13px; font-weight: 600; color: #666; user-select: none; cursor: pointer;
        }
        .esp-toggle {
          position: relative; width: 36px; height: 20px; flex-shrink: 0; cursor: pointer;
        }
        .esp-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
        .esp-toggle .slider {
          position: absolute; inset: 0; background: #ccc; border-radius: 20px;
          transition: background 0.25s;
        }
        .esp-toggle .slider::before {
          content: ''; position: absolute; width: 16px; height: 16px;
          left: 2px; bottom: 2px; background: white; border-radius: 50%;
          transition: transform 0.25s;
        }
        .esp-toggle input:checked + .slider { background: #00aff0; }
        .esp-toggle input:checked + .slider::before { transform: translateX(16px); }

        .esp-view-content-btn {
          padding: 6px 14px; font-size: 13px; font-weight: 600;
          color: #333; background: white; border: 1px solid #ddd;
          border-radius: 5px; cursor: pointer; transition: background 0.2s;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .esp-view-content-btn:hover { background: #f5f5f5; }

        @media (max-width: 1200px) {
          .esp-summary { grid-template-columns: repeat(3, 1fr); }
          .esp-stat-card:nth-child(3n) { border-right: none; }
          .esp-stat-card:nth-child(n+4) { border-top: 1px solid #e5e5e5; }
        }
        @media (max-width: 768px) {
          .esp-summary { grid-template-columns: repeat(2, 1fr); }
          /* Undo the 3-column rule first: card 3 is a left-hand card here. */
          .esp-stat-card:nth-child(3n) { border-right: 1px solid #e5e5e5; }
          .esp-stat-card:nth-child(2n) { border-right: none; }
          .esp-stat-card:nth-child(n+3) { border-top: 1px solid #e5e5e5; }
          .esp-details-grid { grid-template-columns: 1fr; }
          .esp-detail-label { border-bottom: none; padding-bottom: 5px; }
          .esp-detail-value { padding-top: 5px; }
        }
      `}</style>

      <div className="esp-page">
        {/* ── Header ── */}
        <div className="esp-header">
          <div>
            <h1>{campaign.name}</h1>
            <div className="esp-meta">
              <span className={`esp-badge ${statusBadgeClass}`}>
                {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
              </span>
              <span>Sent on {formatSentDate(sentDate, userTz)}</span>
            </div>
            {campaign.description && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#888', lineHeight: 1.5 }}>
                {campaign.description}
              </div>
            )}
          </div>

          <div className="esp-header-right">
            <div className="esp-date-form">
              <label>From:</label>
              <DatePicker
                size="sm"
                ariaLabel="From"
                value={dateFrom}
                onChange={setDateFrom}
                wrapperClassName="w-36"
              />
              <label>To:</label>
              <DatePicker
                size="sm"
                ariaLabel="To"
                value={dateTo}
                onChange={setDateTo}
                wrapperClassName="w-36"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Link to={`/marketing/campaigns/${id}/contacts`} className="esp-contacts-link">
                <span style={{ fontSize: 20, fontWeight: 700, color: '#00aff0' }}>
                  {fmtNum(contactsCount)}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                  {contactsCount === 1 ? 'contact' : 'contacts'}
                </span>
              </Link>
              <button
                type="button"
                className="esp-view-content-btn"
                onClick={() => openContentModal(false)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                View Content
              </button>
              <div className="esp-overflow-wrap" ref={overflowRef}>
                <button
                  type="button"
                  className="esp-overflow-btn"
                  onClick={() => setShowOverflowMenu(v => !v)}
                >
                  <MoreVertical style={{ width: 16, height: 16 }} />
                </button>
                {showOverflowMenu && (
                  <div className="esp-overflow-menu">
                    <button
                      type="button"
                      className="esp-overflow-item"
                      onClick={() => { setShowOverflowMenu(false); setShowCloneModal(true); }}
                    >
                      <Copy style={{ width: 14, height: 14 }} />
                      Clone Campaign
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Summary ── */}
        <div className="esp-summary">
          {STAT_CARDS.map((card) => {
            const val = cardValues[card.key] ?? 0;
            const isZero = val === 0;
            const color = isZero ? undefined : card.color;
            // Every box opens the contact list filtered to the people behind
            // it. Deliberately not lists of their own — a reply or a lead
            // belongs beside that contact's delivery, opens and clicks.
            const box = statBox(isZero ? null : () => navigate(contactsPath(`/marketing/campaigns/${id}/contacts`, card.link)));
            return (
              <div key={card.key} className={cn('esp-stat-card', box.className)} {...box.props}>
                <div
                  className={`esp-stat-value${isZero ? ' zero' : ''}`}
                  style={color ? { color } : undefined}
                >
                  {fmtNum(val)}
                </div>
                <div className="esp-stat-label">{card.label}</div>
              </div>
            );
          })}
        </div>

        {/* Replies from addresses that are not on this campaign match no contact,
            so they are counted above but will NOT appear in the filtered contact
            list. Said explicitly rather than letting the two numbers disagree
            with no explanation. */}
        {(stats.repliesUnattributed ?? 0) > 0 && (
          <div className="esp-replies-note">
            {fmtNum(stats.repliesUnattributed)} of these{' '}
            {stats.repliesUnattributed === 1 ? 'was' : 'were'} sent from an address
            that is not on this campaign, so {stats.repliesUnattributed === 1 ? 'it is' : 'they are'}{' '}
            not linked to a contact and won't appear in the contact list.
          </div>
        )}

        {/* ── Send progress (async send) ── */}
        {campaign?.status === 'sending' && contactsCount > 0 && (
          <div className="esp-actions">
            <span className="esp-actions-title">Sending…</span>
            <span className="esp-actions-note">
              {fmtNum(contactsCount - queuedCount)} of {fmtNum(contactsCount)} handed to the mail
              provider ({Math.round(((contactsCount - queuedCount) / contactsCount) * 100)}%). This
              page refreshes every 30 seconds.
            </span>
          </div>
        )}

        {/* ── Campaign Actions (level 5+) ── */}
        {canManageSend && hasQueued && (
          <div className="esp-actions">
            <span className="esp-actions-title">Campaign Actions</span>
            <span className="esp-actions-note">
              {fmtNum(queuedCount)} contact{queuedCount === 1 ? '' : 's'} queued but not yet sent
            </span>
            <button
              type="button"
              className="esp-action-btn esp-action-btn--resume"
              disabled={resumeMut.isPending}
              onClick={() => setConfirmResume(true)}
            >
              <Play style={{ width: 14, height: 14 }} />
              {resumeMut.isPending ? 'Resuming…' : `Resume Sending (${fmtNum(queuedCount)})`}
            </button>
          </div>
        )}

        {/* ── Chart ── */}
        <div className="esp-chart-container">
          <CampaignDailyChart data={dailyStats} storageKey="marketing.emailCampaignStats.chartType" />
        </div>

        {/* ── Click Tracking ── */}
        <div className="esp-section">
          <div className="esp-section-header">
            Click Tracking
            <span
              className="esp-info-icon"
              title="Shows which links were clicked in your email"
            >
              i
            </span>
            {clickTracking.length > 0 && (
              <button
                type="button"
                className="esp-clickmap-btn"
                onClick={() => openContentModal(true)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 15l-2 5L9 9l11 4-5 2z" />
                  <path d="M22 22l-5-5" />
                </svg>
                View Click Map
              </button>
            )}
          </div>

          {clickTracking.length > 0 ? (
            <>
              <table className="esp-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Rank</th>
                    <th>Link (URL)</th>
                    <th style={{ width: 120 }}>Total Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {clickTracking.map((link, idx) => {
                    const rank = idx + 1;
                    const rankClass =
                      rank === 1
                        ? 'esp-rank-1'
                        : rank === 2
                          ? 'esp-rank-2'
                          : rank === 3
                            ? 'esp-rank-3'
                            : 'esp-rank-default';
                    const pct =
                      (stats.uniqueClicks ?? 0) > 0
                        ? ((link.clicks / stats.uniqueClicks) * 100).toFixed(2)
                        : '0';
                    return (
                      <tr key={idx}>
                        <td>
                          <span className={`esp-rank-badge ${rankClass}`}>{rank}</span>
                        </td>
                        <td>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="esp-link-url"
                          >
                            {link.url}
                          </a>
                        </td>
                        <td>
                          {link.clicks} ({pct}%)
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="esp-table-footer">
                <span>
                  Displaying {clickTracking.length} of {clickTracking.length} links
                </span>
                <span>
                  Total Clicks: <strong>{totalClicks}</strong>
                </span>
              </div>
            </>
          ) : (
            <div className="esp-empty-state">
              <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
              <p>No click tracking data available yet.</p>
            </div>
          )}
        </div>

        {/* ── Details ── */}
        <div className="esp-section">
          <div className="esp-section-header">Details</div>
          <div className="esp-details-grid">
            <div className="esp-detail-row" style={{ display: 'contents' }}>
              <div className="esp-detail-label">Subject Line</div>
              <div className="esp-detail-value">{campaign.subject}</div>
            </div>

            {(campaign.preheader || campaign.pre_header) && (
              <div className="esp-detail-row" style={{ display: 'contents' }}>
                <div className="esp-detail-label">Preheader</div>
                <div className="esp-detail-value" style={{ color: '#888' }}>
                  {campaign.preheader ?? campaign.pre_header}
                </div>
              </div>
            )}

            <div className="esp-detail-row" style={{ display: 'contents' }}>
              <div className="esp-detail-label">Sender</div>
              <div className="esp-detail-value">
                {campaign.from_name ?? campaign.fromName} &lt;
                {campaign.from_email ?? campaign.fromEmail}&gt;
              </div>
            </div>

            {(campaign.unsubscribe_group || campaign.unsubscribeGroup) && (
              <div className="esp-detail-row" style={{ display: 'contents' }}>
                <div className="esp-detail-label">Unsubscribe Group</div>
                <div className="esp-detail-value">
                  {campaign.unsubscribe_group ?? campaign.unsubscribeGroup}
                </div>
              </div>
            )}

            {lists.length > 0 && (
              <div className="esp-detail-row" style={{ display: 'contents' }}>
                <div className="esp-detail-label">Lists</div>
                <div className="esp-detail-value">
                  <div className="esp-list-tags">
                    {lists.map((l) => (
                      <span key={l.id} className="esp-list-tag" title={l.description ?? ''}>
                        {l.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="esp-detail-row" style={{ display: 'contents' }}>
              <div className="esp-detail-label">Recipients</div>
              <div className="esp-detail-value">
                <Link
                  to={`/marketing/campaigns/${id}/contacts`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#00aff0' }}>
                    {fmtNum(contactsCount)}
                  </span>
                  <span>{contactsCount === 1 ? 'contact' : 'contacts'}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── History ── */}
        <MarketingHistoryCard type="email_campaign" id={campaignId} className="mb-[25px]" />
      </div>

      {/* ── Email Content Modal ── */}
      {modalOpen && (
        <div
          className="esp-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setModalOpen(false);
              setClickMapEnabled(false);
            }
          }}
        >
          <div className="esp-modal">
            <div className="esp-modal-header">
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {campaign.subject || '(No subject)'}
                </h3>
                {(campaign.preheader || campaign.pre_header) && (
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {campaign.preheader ?? campaign.pre_header}
                  </div>
                )}
              </div>
              <button
                className="esp-modal-close"
                onClick={() => {
                  setModalOpen(false);
                  setClickMapEnabled(false);
                }}
              >
                &times;
              </button>
            </div>

            <div className="esp-modal-tabs">
              <button
                className={`esp-modal-tab${modalTab === 'html' ? ' active' : ''}`}
                onClick={() => setModalTab('html')}
              >
                HTML Version
              </button>
              <button
                className={`esp-modal-tab${modalTab === 'plain' ? ' active' : ''}`}
                onClick={() => setModalTab('plain')}
              >
                Plain Text Version
              </button>

              <div className="esp-clickmap-toggle-wrap">
                <label className="esp-clickmap-toggle-label" htmlFor="espClickmapToggle">
                  Click Map
                </label>
                <label className="esp-toggle">
                  <input
                    type="checkbox"
                    id="espClickmapToggle"
                    checked={clickMapEnabled}
                    onChange={(e) => handleClickMapToggle(e.target.checked)}
                  />
                  <span className="slider" />
                </label>
              </div>
            </div>

            <div className="esp-modal-body">
              <div className={`esp-modal-panel${modalTab === 'html' ? ' active' : ''}`}>
                <iframe
                  ref={iframeRef}
                  srcDoc={htmlContent || '<p style="padding:20px;color:#999;">No HTML content available.</p>'}
                  sandbox="allow-same-origin"
                />
              </div>
              <div className={`esp-modal-panel${modalTab === 'plain' ? ' active' : ''}`}>
                <pre>{plainContent || '(No plain text content)'}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      <CloneCampaignModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={(cloneName) => cloneMut.mutate(cloneName)}
        loading={cloneMut.isPending}
      />

      {/* Resume Confirmation */}
      {confirmResume && (
        <div className="esp-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmResume(false); }}>
          <div className="esp-confirm-modal">
            <h3>Resume Sending</h3>
            <p>
              This will re-dispatch {fmtNum(queuedCount)} queued contact{queuedCount === 1 ? '' : 's'} to
              the background send queue for delivery. Contacts that were already sent will not be
              duplicated.
            </p>
            <div className="esp-confirm-actions">
              <button type="button" className="esp-confirm-cancel" onClick={() => setConfirmResume(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="esp-action-btn esp-action-btn--resume"
                disabled={resumeMut.isPending}
                onClick={() => resumeMut.mutate()}
              >
                {resumeMut.isPending ? 'Resuming…' : 'Confirm Resume'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
